import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { tryRefreshAuth, markAuthHealthy } from '../lib/firebase/auth';
import type { DirectMessage } from '../types/message';

const DM_COLLECTION = 'directMessages';

/** Subscribe to received DMs (inbox). */
export function subscribeInbox(
  userId: string,
  onUpdate: (msgs: DirectMessage[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, DM_COLLECTION),
    where('recipientId', '==', userId),
    orderBy('sentAt', 'desc'),
    limit(50),
  );

  return onSnapshot(q, (snap) => {
    const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DirectMessage));
    onUpdate(msgs);
  }, (err) => {
    // Silently handle missing index errors to prevent app crash
    console.warn('[DM] Inbox listener error (index may be missing):', err.code);
  });
}

/** Subscribe to sent DMs. */
export function subscribeSent(
  userId: string,
  onUpdate: (msgs: DirectMessage[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, DM_COLLECTION),
    where('senderId', '==', userId),
    orderBy('sentAt', 'desc'),
    limit(50),
  );

  return onSnapshot(q, (snap) => {
    const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DirectMessage));
    onUpdate(msgs);
  }, (err) => {
    console.warn('[DM] Sent listener error (index may be missing):', err.code);
  });
}

/** Send a DM. */
export async function sendDM(params: {
  senderId: string;
  senderNickname: string;
  senderGender?: 'male' | 'female' | 'unknown';
  senderProfileImageUrl?: string;
  recipientId: string;
  recipientNickname: string;
  content: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, DM_COLLECTION), {
    senderId: params.senderId,
    senderNickname: params.senderNickname,
    senderGender: params.senderGender ?? 'unknown',
    senderProfileImageUrl: params.senderProfileImageUrl ?? null,
    recipientId: params.recipientId,
    recipientNickname: params.recipientNickname,
    content: params.content,
    isRead: false,
    sentAt: serverTimestamp(),
  });
  return ref.id;
}

/** Mark a DM as read. */
export async function markAsRead(dmId: string): Promise<void> {
  await updateDoc(doc(db, DM_COLLECTION, dmId), {
    isRead: true,
    readAt: serverTimestamp(),
  });
}

/** Delete a DM. */
export async function deleteDM(dmId: string): Promise<void> {
  await deleteDoc(doc(db, DM_COLLECTION, dmId));
}

/** Delete multiple DMs. */
export async function deleteMultipleDMs(dmIds: string[]): Promise<void> {
  await Promise.all(dmIds.map((id) => deleteDoc(doc(db, DM_COLLECTION, id))));
}

/** Count unread DMs for a user. Auto-retries on permission-denied. */
export function subscribeUnreadCount(
  userId: string,
  onUpdate: (count: number) => void,
): Unsubscribe {
  let innerUnsub: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;
  let retryCount = 0;

  function start() {
    const q = query(
      collection(db, DM_COLLECTION),
      where('recipientId', '==', userId),
      where('isRead', '==', false),
    );

    innerUnsub = onSnapshot(q, (snap) => {
      retryCount = 0;
      if (!snap.metadata.fromCache) markAuthHealthy();
      onUpdate(snap.size);
    }, (err) => {
      console.error('[DM] Unread count listener error:', err.code, err.message);
      if (!cancelled && retryCount < 5 && (err.code === 'permission-denied' || err.code === 'unauthenticated')) {
        const delay = Math.min(3000 * Math.pow(2, retryCount), 30000);
        retryCount++;
        retryTimer = setTimeout(async () => {
          if (cancelled) return;
          const refreshed = await tryRefreshAuth();
          if (!cancelled && refreshed) start();
        }, delay);
      }
    });
  }

  start();

  return () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    innerUnsub?.();
  };
}
